import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { HiArrowLeft } from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';
import { ChannelUser } from '../../../types/service.types';
import Services from '../../../services/Services';
import FormInput from '../../../components/ui/Form/FormInput';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import TagPill from '../../../components/ui/TagPill';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow } from '../../../components/ui/Table';
import { UserCard, UserProfileLink } from '../../../components/ui/UserDisplay';
import styles from './SegmentUsersPage.module.css';

const COLUMN_WIDTHS = ['1fr'];

const SegmentUsersPage: React.FC = () => {
  const { channel } = useOutletContext<OrganizerPanelOutletContext>();
  const { auth, language } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;
  const { tag } = useParams<{ tag: string }>();
  const navigate = useNavigate();

  const [users, setUsers] = useState<ChannelUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const decodedTag = tag ? decodeURIComponent(tag) : '';

  useEffect(() => {
    const fetchUsers = async () => {
      if (!token || !channel?.channelID || !decodedTag) return;

      setLoading(true);
      try {
        const response = await Services.SocialHiveGetUsersBySegment(token, channel.channelID, decodedTag);
        if (response.data.status && response.data.result) {
          setUsers(response.data.result);
        } else {
          setUsers([]);
        }
      } catch {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [token, channel?.channelID, decodedTag]);

  const getUserAvatar = (user: ChannelUser): string | null => {
    return user.userAvatar || user.avatar || user.image || null;
  };

  const getUserName = (user: ChannelUser): string => {
    return `${user.name || ''} ${user.lastname || ''}`.trim() || 'User';
  };

  const getUserEmail = (user: ChannelUser): string | null => {
    return (user as ChannelUser & { email?: string; userEmail?: string }).email ||
      (user as ChannelUser & { email?: string; userEmail?: string }).userEmail || null;
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter((user) => {
      const name = getUserName(user).toLowerCase();
      const email = (getUserEmail(user) || '').toLowerCase();
      return name.includes(query) || email.includes(query);
    });
  }, [users, searchQuery]);

  const handleBack = () => {
    navigate('..', { relative: 'path' });
  };

  return (
    <div className={styles.segmentUsersPage}>
      <div className={styles.header}>
        <button type="button" className={styles.backButton} onClick={handleBack}>
          {/* @ts-ignore - react-icons compatibility with React 19 */}
          <HiArrowLeft size={20} />
        </button>
        <TagPill tag={decodedTag} size="medium" />
        <span className={styles.userCount}>
          {!loading && `${filteredUsers.length} ${(getTranslation('organizerPanel.members') || 'members').toLowerCase()}`}
        </span>
      </div>

      <div className={styles.searchContainer}>
        <FormInput
          type="text"
          placeholder={getTranslation('table.searchMembers') || 'Search members...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
          disabled={loading}
        />
      </div>

      <div className={styles.membersList}>
        <Table>
          <TableHeader
            columns={[
              `${(getTranslation('organizerPanel.members') || 'MEMBERS').toUpperCase()} – ${filteredUsers.length}`,
            ]}
            columnWidths={COLUMN_WIDTHS}
          />
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`} columnWidths={COLUMN_WIDTHS}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <LoadingSkeleton height={40} width={40} variant="circular" />
                      <div style={{ flex: 1 }}>
                        <LoadingSkeleton height={16} width="40%" />
                        <div style={{ marginTop: '4px' }}>
                          <LoadingSkeleton height={14} width="60%" />
                        </div>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : filteredUsers.length === 0 ? (
              <EmptyTableRow
                message={
                  searchQuery
                    ? (getTranslation('organizerPanel.noMembers') || 'No members found')
                    : (getTranslation('organizerPanel.noUsersInSegment') || 'No users in this segment')
                }
              />
            ) : (
              filteredUsers.map((user) => {
                const avatarUrl = getUserAvatar(user);
                const displayName = getUserName(user);
                const userEmail = getUserEmail(user);

                return (
                  <TableRow key={user.id || user.userID} columnWidths={COLUMN_WIDTHS}>
                    <TableCell>
                      <UserProfileLink
                        userId={user.userID || user.id}
                        name={user.name}
                        lastname={user.lastname}
                      >
                        <UserCard
                          avatarUrl={avatarUrl}
                          name={displayName}
                          email={userEmail}
                          size={40}
                        />
                      </UserProfileLink>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SegmentUsersPage;
